import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Wrench, Truck, AlertTriangle, Plus, Gauge, ClipboardList, Trash2 } from 'lucide-react';

import { BulkLastServiceDialog, VehicleKmLogDialog } from '@/components/fleet/FleetKmDialogs';
import { AlertSettingsCard, FaultPhotoThumbs, OdometerMismatches } from '@/components/fleet/FleetExtras';
import { FleetClassSelect, saveFleetClass } from '@/components/fuel/FleetClassPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLOR = {
  green:  'bg-emerald-500 text-white',
  orange: 'bg-amber-500 text-white',
  red:    'bg-rose-600 text-white',
  unset:  'bg-amber-100 text-amber-800 border border-amber-300',
};

const STATUS_LABEL = {
  green:  'OK',
  orange: 'Due soon',
  red:    'OVERDUE',
  unset:  'Service km not set',
};

const emptyVehicle = { registration: '', vehicle: 'Nissan NP200', engine: '', current_mileage: 0, last_service_mileage: 0, assigned_driver_id: '', assigned_driver_source: '' };
const emptyMileage = { current_mileage: 0 };
const emptyService = { service_code: 'S12', date: '', mileage: 0, work_done: '', parts_used: '', cost: 0, workshop: '', notes: '' };
const emptyFault   = { description: '', severity: 'medium', service_code: '', mileage: 0, notes: '' };
const emptyJobCard = { scheduled_date: '', service_code: 'S12', description: '', workshop: '', estimated_cost: 0, priority: 'medium' };

export default function FleetMaintenance() {
  const [tab, setTab] = useState('dashboard');
  const [dash, setDash] = useState(null);
  const [services, setServices] = useState([]);
  const [faults, setFaults] = useState([]);
  const [jobCards, setJobCards] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');

  // Dialogs
  const [addVehicleOpen, setAddVehicleOpen] = useState(false);
  const [addVehicleForm, setAddVehicleForm] = useState(emptyVehicle);
  const [editingVehicle, setEditingVehicle] = useState(null);   // holds the row when re-opening the dialog
  const [drivers, setDrivers] = useState([]);
  const [mileageForVehicle, setMileageForVehicle] = useState(null);
  const [mileageForm, setMileageForm] = useState(emptyMileage);
  const [serviceForVehicle, setServiceForVehicle] = useState(null);
  const [serviceForm, setServiceForm] = useState(emptyService);
  const [faultForVehicle, setFaultForVehicle] = useState(null);
  const [faultForm, setFaultForm] = useState(emptyFault);
  const [jobCardForVehicle, setJobCardForVehicle] = useState(null);
  const [jobCardForm, setJobCardForm] = useState(emptyJobCard);
  const [jobCardFromFault, setJobCardFromFault] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [bulkOpen, setBulkOpen] = useState(false);
  const [kmLogVehicle, setKmLogVehicle] = useState(null);
  const [fleetClasses, setFleetClasses] = useState({});

  const fetchFleetClasses = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fuel-tracker/fleet-classes`, { headers });
      setFleetClasses(Object.fromEntries(res.data.map(r => [r.registration, r.fleet_class])));
    } catch { /* fuel-tracker permission not granted: column shows default */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchFleetClasses(); }, [fetchFleetClasses]);

  const changeFleetClass = async (reg, fc) => {
    try { await saveFleetClass(headers, [reg], fc); fetchFleetClasses(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not update fleet class'); }
  };

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fleet/dashboard`, { headers });
      setDash(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load fleet');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const q = selectedVehicle ? `?vehicle_id=${selectedVehicle}` : '';
      const res = await axios.get(`${API_URL}/api/fleet/service-records${q}`, { headers });
      setServices(res.data || []);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  const fetchFaults = useCallback(async () => {
    try {
      const q = selectedVehicle ? `?vehicle_id=${selectedVehicle}` : '';
      const res = await axios.get(`${API_URL}/api/fleet/faults${q}`, { headers });
      setFaults(res.data || []);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  const fetchJobCards = useCallback(async () => {
    try {
      const q = selectedVehicle ? `?vehicle_id=${selectedVehicle}` : '';
      const res = await axios.get(`${API_URL}/api/fleet/job-cards${q}`, { headers });
      setJobCards(res.data || []);
    } catch { /* silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle]);

  useEffect(() => { fetchDashboard(); fetchServices(); fetchFaults(); fetchJobCards(); }, [fetchDashboard, fetchServices, fetchFaults, fetchJobCards]);

  // Assignable drivers (internal_staff + drivers) for the vehicle-driver picker.
  const fetchDrivers = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fleet/assignable-drivers`, { headers });
      setDrivers(Array.isArray(res.data) ? res.data : []);
    } catch { /* silent — dropdown just stays empty */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchDrivers(); }, [fetchDrivers]);

  const openNewVehicleDialog = () => {
    setEditingVehicle(null);
    setAddVehicleForm(emptyVehicle);
    setAddVehicleOpen(true);
  };
  const openEditVehicleDialog = (v) => {
    setEditingVehicle(v);
    setAddVehicleForm({
      registration: v.registration || '',
      vehicle: v.vehicle || 'Nissan NP200',
      engine: v.engine || '',
      current_mileage: v.current_mileage || 0,
      last_service_mileage: v.last_service_mileage || 0,
      assigned_driver_id: v.assigned_driver_id || '',
      assigned_driver_source: v.assigned_driver_source || '',
    });
    setAddVehicleOpen(true);
  };

  const vehicles = dash?.vehicles || [];
  const codes = dash?.service_codes || {};
  const codeEntries = useMemo(() => Object.entries(codes), [codes]);
  const s12Checklist = dash?.s12_checklist || [];

  const R = (n) => 'R ' + Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ---- Actions ----
  const createVehicle = async () => {
    if (!editingVehicle && !addVehicleForm.registration.trim()) { toast.error('Registration required'); return; }
    try {
      if (editingVehicle) {
        // Update — only send editable fields (registration is immutable server-side).
        const payload = {
          vehicle: addVehicleForm.vehicle,
          engine: addVehicleForm.engine,
          current_mileage: addVehicleForm.current_mileage,
          last_service_mileage: addVehicleForm.last_service_mileage,
          assigned_driver_id: addVehicleForm.assigned_driver_id || '',
        };
        await axios.put(`${API_URL}/api/fleet/vehicles/${editingVehicle.id}`, payload, { headers });
        toast.success('Vehicle updated');
      } else {
        await axios.post(`${API_URL}/api/fleet/vehicles`, addVehicleForm, { headers });
        toast.success('Vehicle added');
      }
      setAddVehicleOpen(false); setAddVehicleForm(emptyVehicle); setEditingVehicle(null);
      fetchDashboard(); fetchDrivers();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const deleteVehicle = async (v) => {
    if (!window.confirm(`Delete ${v.registration} and all its records?`)) return;
    try { await axios.delete(`${API_URL}/api/fleet/vehicles/${v.id}`, { headers }); toast.success('Deleted'); fetchDashboard(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const openMileage = (v) => { setMileageForVehicle(v); setMileageForm({ current_mileage: v.current_mileage }); };
  const saveMileage = async () => {
    try {
      await axios.post(`${API_URL}/api/fleet/vehicles/${mileageForVehicle.id}/update-mileage`, { current_mileage: parseInt(mileageForm.current_mileage, 10) }, { headers });
      toast.success('Mileage updated');
      setMileageForVehicle(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Update failed'); }
  };

  const openService = (v, prefillCode) => {
    setServiceForVehicle(v);
    setServiceForm({
      ...emptyService,
      service_code: prefillCode || 'S12',
      mileage: v.current_mileage,
      date: new Date().toISOString().slice(0, 10),
      work_done: prefillCode === 'S12' || !prefillCode ? s12Checklist.join('; ') : '',
    });
  };
  const saveService = async () => {
    try {
      await axios.post(`${API_URL}/api/fleet/service-records`, {
        ...serviceForm,
        vehicle_id: serviceForVehicle.id,
        mileage: parseInt(serviceForm.mileage, 10),
        cost: parseFloat(serviceForm.cost || 0),
      }, { headers });
      toast.success('Service recorded');
      setServiceForVehicle(null);
      fetchDashboard(); fetchServices(); fetchJobCards();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const openFault = (v) => {
    setFaultForVehicle(v);
    setFaultForm({ ...emptyFault, mileage: v.current_mileage });
  };
  const saveFault = async () => {
    if (!faultForm.description.trim()) { toast.error('Describe the fault'); return; }
    try {
      await axios.post(`${API_URL}/api/fleet/faults`, {
        ...faultForm,
        vehicle_id: faultForVehicle.id,
        mileage: parseInt(faultForm.mileage, 10),
      }, { headers });
      toast.success('Fault logged');
      setFaultForVehicle(null);
      fetchFaults(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const openJobCard = (v, fault) => {
    setJobCardForVehicle(v);
    setJobCardFromFault(fault || null);
    setJobCardForm({
      ...emptyJobCard,
      scheduled_date: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
      service_code: fault?.service_code || 'S12',
      description: fault?.description || '',
      priority: fault?.severity === 'high' ? 'high' : (fault?.severity || 'medium'),
    });
  };
  const saveJobCard = async () => {
    try {
      await axios.post(`${API_URL}/api/fleet/job-cards`, {
        ...jobCardForm,
        vehicle_id: jobCardForVehicle.id,
        fault_ids: jobCardFromFault ? [jobCardFromFault.id] : [],
        estimated_cost: parseFloat(jobCardForm.estimated_cost || 0),
      }, { headers });
      toast.success('Job card scheduled');
      setJobCardForVehicle(null); setJobCardFromFault(null);
      fetchJobCards(); fetchFaults(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const completeJobCard = async (jc) => {
    // Convert to a service record (auto-advances 12k km if S12)
    const v = vehicles.find(x => x.id === jc.vehicle_id);
    if (!v) return;
    openService(v, jc.service_code);
    setServiceForm(prev => ({ ...prev, job_card_id: jc.id, work_done: jc.description }));
  };

  const nextDue = dash?.next_due;

  return (
    <DashboardLayout>
      <div className="space-y-4 pb-8">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Wrench className="h-6 w-6 text-blue-600" /> Fleet Maintenance
            </h1>
            <p className="text-sm text-slate-500">Nissan NP200 · 12,000 km service interval. Overdue vehicles listed first.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-amber-400 text-amber-800" data-testid="fleet-bulk-last-service-btn">
              <Gauge className="h-4 w-4 mr-1" /> Set last service km{dash?.counts?.unset ? ` (${dash.counts.unset} not set)` : ''}
            </Button>
            <Button onClick={openNewVehicleDialog} className="bg-blue-600 hover:bg-blue-700" data-testid="fleet-add-vehicle-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Vehicle
            </Button>
          </div>
        </div>
        <AlertSettingsCard headers={headers} />
        <BulkLastServiceDialog open={bulkOpen} onOpenChange={setBulkOpen} vehicles={vehicles} headers={headers} onSaved={fetchDashboard} />
        <VehicleKmLogDialog vehicle={kmLogVehicle} onClose={() => setKmLogVehicle(null)} headers={headers} />

        {/* Alert banner: next bakkie due */}
        {nextDue && (
          <Card className={`border-l-4 ${nextDue.status === 'red' ? 'border-l-rose-600 bg-rose-50' : 'border-l-amber-500 bg-amber-50'}`} data-testid="fleet-next-due-banner">
            <CardContent className="p-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-5 w-5 ${nextDue.status === 'red' ? 'text-rose-600' : 'text-amber-600'}`} />
                <div>
                  <div className="text-sm text-slate-500">Next bakkie due for {nextDue.status === 'red' ? 'IMMEDIATE service' : 'service soon'}</div>
                  <div className="text-lg font-bold text-slate-800">
                    {nextDue.registration} · {nextDue.vehicle}
                    <span className={`ml-2 ${nextDue.status === 'red' ? 'text-rose-700' : 'text-amber-700'}`}>
                      {nextDue.km_remaining <= 0 ? `${Math.abs(nextDue.km_remaining)} km overdue` : `${nextDue.km_remaining.toLocaleString('en-ZA')} km remaining`}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => openMileage(nextDue)} data-testid="fleet-quick-mileage">
                  <Gauge className="h-4 w-4 mr-1" /> Update KM
                </Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openService(nextDue, 'S12')} data-testid="fleet-quick-service">
                  <Wrench className="h-4 w-4 mr-1" /> Book Service
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary tiles */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><CardContent className="p-3">
            <div className="text-xs text-slate-500 uppercase">Overdue</div>
            <div className="text-2xl font-bold text-rose-600" data-testid="fleet-count-red">{dash?.counts?.red ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-slate-500 uppercase">Due Soon (≤1,000 km)</div>
            <div className="text-2xl font-bold text-amber-600" data-testid="fleet-count-orange">{dash?.counts?.orange ?? 0}</div>
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-slate-500 uppercase">Healthy</div>
            <div className="text-2xl font-bold text-emerald-600" data-testid="fleet-count-green">{dash?.counts?.green ?? 0}</div>
            {dash?.counts?.unset > 0 && <div className="text-[11px] text-amber-700" data-testid="fleet-count-unset">{dash.counts.unset} with service km not set</div>}
          </CardContent></Card>
          <Card><CardContent className="p-3">
            <div className="text-xs text-slate-500 uppercase">Open Faults / Scheduled Jobs</div>
            <div className="text-2xl font-bold text-slate-700"><span data-testid="fleet-open-faults">{dash?.open_faults ?? 0}</span> / <span data-testid="fleet-scheduled-jobs">{dash?.scheduled_jobs ?? 0}</span></div>
          </CardContent></Card>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full flex flex-wrap">
            <TabsTrigger value="dashboard" data-testid="fleet-tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="services" data-testid="fleet-tab-services">Service History</TabsTrigger>
            <TabsTrigger value="faults" data-testid="fleet-tab-faults">Faults ({faults.filter(f => f.status !== 'resolved').length})</TabsTrigger>
            <TabsTrigger value="jobs" data-testid="fleet-tab-jobs">Job Cards ({jobCards.filter(j => j.status !== 'completed').length})</TabsTrigger>
            <TabsTrigger value="odometer" data-testid="fleet-tab-odometer">Odometer Mismatches</TabsTrigger>
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Vehicles</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {vehicles.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 text-sm">No vehicles yet. Add one to start tracking.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Fuel Class</TableHead>
                        <TableHead>Assigned Driver</TableHead>
                        <TableHead className="text-right">Current KM</TableHead>
                        <TableHead className="text-right">KM driven wk / mth</TableHead>
                        <TableHead className="text-right">Avg / day</TableHead>
                        <TableHead className="text-right">Last Service</TableHead>
                        <TableHead className="text-right">Next Service</TableHead>
                        <TableHead className="text-right">KM Remaining</TableHead>
                        <TableHead>Est. Service Date</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {vehicles.map(v => (
                        <TableRow key={v.id} data-testid={`fleet-row-${v.registration}`}>
                          <TableCell>
                            <div className="font-semibold text-slate-800">{v.registration}</div>
                            <div className="text-xs text-slate-500">{v.vehicle}{v.engine ? ` · ${v.engine}` : ''}</div>
                          </TableCell>
                          <TableCell>
                            <FleetClassSelect value={fleetClasses[(v.registration || '').toUpperCase()] || 'internal'} onChange={(fc) => changeFleetClass(v.registration, fc)} testId={`fleet-class-select-${v.registration}`} />
                          </TableCell>
                          <TableCell>
                            {v.assigned_driver_name ? (
                              <div className="text-sm font-medium text-indigo-700" data-testid={`assigned-driver-${v.registration}`}>
                                {v.assigned_driver_name}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400 italic">unassigned</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono">{v.current_mileage.toLocaleString('en-ZA')}</TableCell>
                          <TableCell className="text-right font-mono text-sm" data-testid={`km-driven-${v.registration}`}>{(v.km_week || 0).toLocaleString('en-ZA')} / {(v.km_month || 0).toLocaleString('en-ZA')}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{v.avg_km_per_day != null ? v.avg_km_per_day.toLocaleString('en-ZA') : '—'}</TableCell>
                          <TableCell className="text-right font-mono text-sm">{v.last_service_set ? v.last_service_mileage.toLocaleString('en-ZA') : <span className="text-amber-700 text-xs">not set</span>}</TableCell>
                          <TableCell className="text-right font-mono">{v.next_service_mileage != null ? v.next_service_mileage.toLocaleString('en-ZA') : '—'}</TableCell>
                          <TableCell className={`text-right font-mono font-semibold ${v.status === 'red' ? 'text-rose-600' : v.status === 'orange' ? 'text-amber-600' : v.status === 'unset' ? 'text-amber-700' : 'text-emerald-600'}`}>
                            {v.km_remaining == null ? '—' : v.km_remaining <= 0 ? `-${Math.abs(v.km_remaining).toLocaleString('en-ZA')}` : v.km_remaining.toLocaleString('en-ZA')}
                          </TableCell>
                          <TableCell className="text-sm whitespace-nowrap" data-testid={`est-service-${v.registration}`}>{v.estimated_service_date || '—'}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLOR[v.status]}>{STATUS_LABEL[v.status]}</Badge>
                          </TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            <div className="inline-flex flex-wrap gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => openEditVehicleDialog(v)} data-testid={`edit-vehicle-${v.registration}`}>
                                Edit
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setKmLogVehicle(v)} data-testid={`km-log-${v.registration}`}>
                                History
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openMileage(v)} data-testid={`update-mileage-${v.registration}`}>
                                <Gauge className="h-3.5 w-3.5 mr-1" /> KM
                              </Button>
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => openService(v, 'S12')} data-testid={`add-service-${v.registration}`}>
                                <Wrench className="h-3.5 w-3.5 mr-1" /> Service
                              </Button>
                              <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={() => openService(v, 'EMR')} data-testid={`add-repair-${v.registration}`}>
                                <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Repair
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => openFault(v)} data-testid={`add-fault-${v.registration}`}>
                                Fault
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteVehicle(v)} data-testid={`delete-vehicle-${v.registration}`}>
                                <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Service History */}
          <TabsContent value="services">
            <Card>
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-base">Service & Repair History</CardTitle>
                <Select value={selectedVehicle || 'all'} onValueChange={(v) => setSelectedVehicle(v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-56 h-8 text-xs" data-testid="fleet-filter-vehicle"><SelectValue placeholder="All vehicles" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All vehicles</SelectItem>
                    {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.registration}</SelectItem>)}
                  </SelectContent>
                </Select>
              </CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {services.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 text-sm">No service history yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead className="text-right">Mileage</TableHead>
                        <TableHead>Work Done</TableHead>
                        <TableHead>Workshop</TableHead>
                        <TableHead className="text-right">Cost</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.map(s => (
                        <TableRow key={s.id} data-testid={`service-row-${s.id}`}>
                          <TableCell className="text-xs">{s.date}</TableCell>
                          <TableCell className="font-semibold">{s.registration}</TableCell>
                          <TableCell><Badge variant="outline">{s.service_code}</Badge></TableCell>
                          <TableCell className="text-right font-mono">{Number(s.mileage).toLocaleString('en-ZA')}</TableCell>
                          <TableCell className="text-xs max-w-[300px] truncate" title={s.work_done}>{s.work_done || '—'}</TableCell>
                          <TableCell className="text-xs">{s.workshop || '—'}</TableCell>
                          <TableCell className="text-right font-mono">{R(s.cost)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Faults */}
          <TabsContent value="odometer"><OdometerMismatches headers={headers} onChanged={fetchDashboard} /></TabsContent>

          <TabsContent value="faults">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Reported Faults</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {faults.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 text-sm">No faults reported.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Reported</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {faults.map(f => (
                        <TableRow key={f.id} data-testid={`fault-row-${f.id}`} className={f.status === 'resolved' ? 'opacity-60' : ''}>
                          <TableCell className="text-xs">{f.reported_at?.slice(0, 10)}</TableCell>
                          <TableCell className="font-semibold">{f.registration}</TableCell>
                          <TableCell>
                            <Badge className={f.severity === 'high' ? 'bg-rose-600 text-white' : f.severity === 'medium' ? 'bg-amber-500 text-white' : 'bg-slate-500 text-white'}>
                              {f.severity}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs max-w-[320px]">{f.description}<FaultPhotoThumbs faults={[f]} headers={headers} /></TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{f.status}</Badge></TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {f.status !== 'resolved' && (
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => openJobCard(vehicles.find(v => v.id === f.vehicle_id), f)}
                                data-testid={`schedule-jc-${f.id}`}>
                                <ClipboardList className="h-3.5 w-3.5 mr-1" /> Schedule Job Card
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Job Cards */}
          <TabsContent value="jobs">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Scheduled Job Cards</CardTitle></CardHeader>
              <CardContent className="p-0 overflow-x-auto">
                {jobCards.length === 0 ? (
                  <p className="text-center py-8 text-slate-500 text-sm">No job cards scheduled.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Job #</TableHead>
                        <TableHead>Scheduled</TableHead>
                        <TableHead>Vehicle</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Workshop</TableHead>
                        <TableHead>Priority</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobCards.map(j => (
                        <TableRow key={j.id} data-testid={`jc-row-${j.id}`} className={j.status === 'completed' ? 'opacity-60' : j.priority === 'high' ? 'bg-rose-50' : ''}>
                          <TableCell className="text-xs font-mono">{j.job_number}</TableCell>
                          <TableCell className="text-xs">{j.scheduled_date}</TableCell>
                          <TableCell className="font-semibold">{j.registration}</TableCell>
                          <TableCell><Badge variant="outline">{j.service_code}</Badge></TableCell>
                          <TableCell className="text-xs max-w-[280px]" title={j.description}><div className="truncate">{j.description || '—'}</div><FaultPhotoThumbs faults={faults.filter((f) => (j.fault_ids || []).includes(f.id))} headers={headers} /></TableCell>
                          <TableCell className="text-xs">{j.workshop || '—'}</TableCell>
                          <TableCell><Badge className={j.priority === 'high' ? 'bg-rose-600 text-white' : j.priority === 'medium' ? 'bg-amber-500 text-white' : 'bg-slate-500 text-white'}>{j.priority}</Badge></TableCell>
                          <TableCell><Badge variant="outline" className="capitalize">{j.status.replace('_', ' ')}</Badge></TableCell>
                          <TableCell className="text-right whitespace-nowrap">
                            {j.status !== 'completed' && (
                              <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => completeJobCard(j)} data-testid={`complete-jc-${j.id}`}>
                                Complete
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Add / Edit Vehicle Dialog */}
      <Dialog open={addVehicleOpen} onOpenChange={(o) => { setAddVehicleOpen(o); if (!o) setEditingVehicle(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editingVehicle ? `Edit Vehicle — ${editingVehicle.registration}` : 'Add Vehicle'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Registration *</Label>
              <Input
                value={addVehicleForm.registration}
                onChange={(e) => setAddVehicleForm({ ...addVehicleForm, registration: e.target.value.toUpperCase() })}
                disabled={!!editingVehicle}
                data-testid="fleet-add-reg"
              />
            </div>
            <div><Label>Vehicle</Label><Input value={addVehicleForm.vehicle} onChange={(e) => setAddVehicleForm({ ...addVehicleForm, vehicle: e.target.value })} data-testid="fleet-add-vehicle" /></div>
            <div><Label>Engine</Label><Input value={addVehicleForm.engine} onChange={(e) => setAddVehicleForm({ ...addVehicleForm, engine: e.target.value })} placeholder="e.g. 1.6i" data-testid="fleet-add-engine" /></div>
            <div>
              <Label>Assigned Driver</Label>
              <Select
                value={addVehicleForm.assigned_driver_id || '__none'}
                onValueChange={(v) => setAddVehicleForm({ ...addVehicleForm, assigned_driver_id: v === '__none' ? '' : v })}
              >
                <SelectTrigger data-testid="fleet-add-driver"><SelectValue placeholder="— unassigned —" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none">— unassigned —</SelectItem>
                  {(() => {
                    const internal = drivers.filter((d) => d.role === 'internal_staff');
                    const staffDriver = drivers.filter((d) => d.role === 'driver');
                    const owners = drivers.filter((d) => d.role === 'owner_driver');
                    const renderGroup = (label, list) => list.length > 0 && (
                      <div key={label}>
                        <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-slate-500 bg-slate-50 border-b">
                          {label} ({list.length})
                        </div>
                        {list.map((d) => {
                          const taggedElsewhere = d.assigned_vehicle_id && d.assigned_vehicle_id !== editingVehicle?.id;
                          return (
                            <SelectItem key={`${d.source || 'user'}-${d.id}`} value={d.id}>
                              {d.full_name}
                              {taggedElsewhere ? `  (on ${d.assigned_vehicle_registration || 'another'})` : ''}
                            </SelectItem>
                          );
                        })}
                      </div>
                    );
                    return [
                      renderGroup('Internal Staff', internal),
                      renderGroup('Drivers (accounts)', staffDriver),
                      renderGroup('Owner-Drivers', owners),
                    ];
                  })()}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Assigning here also stamps the vehicle onto the driver's profile so it follows them across staff, payslip and portal views.</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Current Mileage</Label><Input type="number" value={addVehicleForm.current_mileage} onChange={(e) => setAddVehicleForm({ ...addVehicleForm, current_mileage: parseInt(e.target.value || 0, 10) })} data-testid="fleet-add-current-km" /></div>
              <div><Label>Last Service Mileage</Label><Input type="number" value={addVehicleForm.last_service_mileage} onChange={(e) => setAddVehicleForm({ ...addVehicleForm, last_service_mileage: parseInt(e.target.value || 0, 10) })} data-testid="fleet-add-last-service-km" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAddVehicleOpen(false); setEditingVehicle(null); }}>Cancel</Button>
            <Button onClick={createVehicle} className="bg-blue-600 hover:bg-blue-700" data-testid="fleet-add-vehicle-save">{editingVehicle ? 'Update' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Mileage Dialog */}
      <Dialog open={!!mileageForVehicle} onOpenChange={(o) => !o && setMileageForVehicle(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Update Mileage — {mileageForVehicle?.registration}</DialogTitle></DialogHeader>
          <div>
            <Label>Current Mileage (km)</Label>
            <Input type="number" value={mileageForm.current_mileage}
              onChange={(e) => setMileageForm({ current_mileage: parseInt(e.target.value || 0, 10) })}
              data-testid="fleet-mileage-input" />
            <p className="text-xs text-slate-500 mt-1">Cannot roll backwards. Fuel entries with a km reading update this automatically.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMileageForVehicle(null)}>Cancel</Button>
            <Button onClick={saveMileage} className="bg-blue-600 hover:bg-blue-700" data-testid="fleet-mileage-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Service / Repair Dialog */}
      <Dialog open={!!serviceForVehicle} onOpenChange={(o) => !o && setServiceForVehicle(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{serviceForm.service_code === 'S12' ? 'Book 12k Service' : 'Add Repair / Service'} — {serviceForVehicle?.registration}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Service Code</Label>
                <Select value={serviceForm.service_code} onValueChange={(v) => setServiceForm({ ...serviceForm, service_code: v, work_done: v === 'S12' ? s12Checklist.join('; ') : serviceForm.work_done })}>
                  <SelectTrigger data-testid="fleet-service-code"><SelectValue /></SelectTrigger>
                  <SelectContent>{codeEntries.map(([k, v]) => <SelectItem key={k} value={k}>{k} — {v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Date</Label><Input type="date" value={serviceForm.date} onChange={(e) => setServiceForm({ ...serviceForm, date: e.target.value })} data-testid="fleet-service-date" /></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Mileage</Label><Input type="number" value={serviceForm.mileage} onChange={(e) => setServiceForm({ ...serviceForm, mileage: parseInt(e.target.value || 0, 10) })} data-testid="fleet-service-mileage" /></div>
              <div><Label>Cost (R)</Label><Input type="number" step="0.01" value={serviceForm.cost} onChange={(e) => setServiceForm({ ...serviceForm, cost: e.target.value })} data-testid="fleet-service-cost" /></div>
            </div>
            <div><Label>Workshop / Mechanic</Label><Input value={serviceForm.workshop} onChange={(e) => setServiceForm({ ...serviceForm, workshop: e.target.value })} data-testid="fleet-service-workshop" /></div>
            <div><Label>Work Done</Label><Textarea rows={3} value={serviceForm.work_done} onChange={(e) => setServiceForm({ ...serviceForm, work_done: e.target.value })} data-testid="fleet-service-work-done" /></div>
            <div><Label>Parts Used</Label><Textarea rows={2} value={serviceForm.parts_used} onChange={(e) => setServiceForm({ ...serviceForm, parts_used: e.target.value })} data-testid="fleet-service-parts" /></div>
            <div><Label>Notes</Label><Textarea rows={2} value={serviceForm.notes} onChange={(e) => setServiceForm({ ...serviceForm, notes: e.target.value })} /></div>
            {serviceForm.service_code === 'S12' && (
              <p className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                Saving as S12 will auto-advance next service to <b>{(parseInt(serviceForm.mileage || 0, 10) + 12000).toLocaleString('en-ZA')} km</b>.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setServiceForVehicle(null)}>Cancel</Button>
            <Button onClick={saveService} className="bg-emerald-600 hover:bg-emerald-700" data-testid="fleet-service-save">Save Record</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Fault Dialog */}
      <Dialog open={!!faultForVehicle} onOpenChange={(o) => !o && setFaultForVehicle(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Report Fault — {faultForVehicle?.registration}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Description *</Label><Textarea rows={3} value={faultForm.description} onChange={(e) => setFaultForm({ ...faultForm, description: e.target.value })} placeholder="e.g. Vibration at 90 km/h, brakes squealing when cold…" data-testid="fleet-fault-desc" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Severity</Label>
                <Select value={faultForm.severity} onValueChange={(v) => setFaultForm({ ...faultForm, severity: v })}>
                  <SelectTrigger data-testid="fleet-fault-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category (optional)</Label>
                <Select value={faultForm.service_code || 'none'} onValueChange={(v) => setFaultForm({ ...faultForm, service_code: v === 'none' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Any" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">—</SelectItem>
                    {codeEntries.map(([k, v]) => <SelectItem key={k} value={k}>{k} — {v}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Notes</Label><Textarea rows={2} value={faultForm.notes} onChange={(e) => setFaultForm({ ...faultForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFaultForVehicle(null)}>Cancel</Button>
            <Button onClick={saveFault} className="bg-amber-600 hover:bg-amber-700" data-testid="fleet-fault-save">Log Fault</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Job Card Dialog */}
      <Dialog open={!!jobCardForVehicle} onOpenChange={(o) => !o && setJobCardForVehicle(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Schedule Job Card — {jobCardForVehicle?.registration}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Scheduled Date</Label><Input type="date" value={jobCardForm.scheduled_date} onChange={(e) => setJobCardForm({ ...jobCardForm, scheduled_date: e.target.value })} data-testid="fleet-jc-date" /></div>
              <div>
                <Label>Service Code</Label>
                <Select value={jobCardForm.service_code} onValueChange={(v) => setJobCardForm({ ...jobCardForm, service_code: v })}>
                  <SelectTrigger data-testid="fleet-jc-code"><SelectValue /></SelectTrigger>
                  <SelectContent>{codeEntries.map(([k, v]) => <SelectItem key={k} value={k}>{k} — {v}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Description</Label><Textarea rows={3} value={jobCardForm.description} onChange={(e) => setJobCardForm({ ...jobCardForm, description: e.target.value })} data-testid="fleet-jc-desc" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Workshop</Label><Input value={jobCardForm.workshop} onChange={(e) => setJobCardForm({ ...jobCardForm, workshop: e.target.value })} data-testid="fleet-jc-workshop" /></div>
              <div><Label>Estimated Cost (R)</Label><Input type="number" step="0.01" value={jobCardForm.estimated_cost} onChange={(e) => setJobCardForm({ ...jobCardForm, estimated_cost: e.target.value })} data-testid="fleet-jc-cost" /></div>
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={jobCardForm.priority} onValueChange={(v) => setJobCardForm({ ...jobCardForm, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJobCardForVehicle(null)}>Cancel</Button>
            <Button onClick={saveJobCard} className="bg-blue-600 hover:bg-blue-700" data-testid="fleet-jc-save">Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
